/**
 * Funding & Growth (TRUS-776) — the org-scoped opportunity finder and funder
 * outreach, at /api/funding-opportunities.
 *
 * Every call carries the settings page's organization as X-CBO-ID. authFetch
 * keeps a per-call X-CBO-ID, so an admin viewing org N's settings reads and
 * writes org N — not their own active org (the cross-org trap the axios
 * clients fell into, FE #801).
 *
 * Types live here and nowhere else, so a change in the backend's shapes is one
 * edit. Every route returns 403 when the org does not have the
 * `funding_opportunities` capability; callers gate on the capability first.
 */
import { authFetchJson } from '@/lib/authFetch';
import { getWeaveinApiUrl } from '@/src/config';

export type CapabilityKind = 'provider_service' | 'program';
export type CapabilityItemStatus = 'proposed' | 'confirmed' | 'rejected';
export type IntentSourceKind = 'payer_directory' | 'program_catalog' | 'discovered_sponsors' | 'manual';
export type OpportunityStatus =
  | 'new'
  | 'reviewing'
  | 'pursuing'
  | 'outreach_drafted'
  | 'outreach_sent'
  | 'responded'
  | 'won'
  | 'dismissed';
export type OutreachStatus = 'draft' | 'approved' | 'sent' | 'failed';

export interface CapabilityProfile {
  organization_id: number;
  service_states: string[];
  service_counties: string[];
  languages: string[];
  npis: string[];
  provider_types: string[];
  notes?: string | null;
  reviewed_by?: number | null;
  reviewed_at?: string | null;
  updated_at?: string | null;
}

export interface CapabilityItem {
  id: number;
  organization_id: number;
  kind: CapabilityKind;
  label: string;
  service_codes: string[];
  program_types: string[];
  program_id?: number | null;
  source: 'derived' | 'program_import' | 'manual';
  status: CapabilityItemStatus;
  /** Why it was derived: free-form; shown as plain sentences when it has `reasons`. */
  evidence?: { reasons?: string[]; [k: string]: unknown } | null;
  confirmed_at?: string | null;
}

export interface IntentSource {
  id: number;
  kind: IntentSourceKind;
  enabled: boolean;
  config?: Record<string, unknown> | null;
  last_scanned_at?: string | null;
  last_found?: number | null;
}

export interface MatchReason {
  reason: string;
  weight?: number;
}

export interface Opportunity {
  id: number;
  source_kind: IntentSourceKind;
  source_ref: string;
  funder_name: string;
  funder_kind: string;
  payer_id?: number | null;
  catalog_id?: number | null;
  sponsor_id?: number | null;
  is_existing: boolean;
  match_score: number;
  match_reasons: MatchReason[];
  matched_item_ids: number[];
  deadline?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  status: OpportunityStatus;
  status_note?: string | null;
  updated_at?: string | null;
}

export interface Outreach {
  id: number;
  opportunity_id: number;
  channel: 'email';
  to_name?: string | null;
  to_email?: string | null;
  subject: string;
  body: string;
  drafted_by: 'agent' | 'user';
  status: OutreachStatus;
  approved_at?: string | null;
  sent_at?: string | null;
  error?: string | null;
  created_at?: string | null;
  /** Convenience field the list may include; falls back to the opportunity lookup. */
  funder_name?: string | null;
}

export interface AttributionFunder {
  funder_name: string;
  is_existing: boolean;
  opportunity_id?: number | null;
  payer_id?: number | null;
  /** How a claim to this funder would go out, as the submission router resolves it. */
  channel?: string | null;
  program?: string | null;
  status?: string | null;
}

export interface AttributionResult {
  service_code?: string | null;
  item_id?: number | null;
  label?: string | null;
  funders: AttributionFunder[];
}

export interface FundingSummary {
  capabilities: { confirmed: number; proposed: number };
  opportunities: { new: number; pursuing: number; existing: number };
  outreach: { draft: number; sent: number };
}

export interface ScanResult {
  found: number;
  new: number;
  existing: number;
  by_source: Record<string, number>;
}

const base = (path: string) => getWeaveinApiUrl(`/api/funding-opportunities${path}`);

/**
 * The backend wraps every list ({capabilities: [...]}, {opportunities: [...]}).
 * Unwrap here, once, so the panels work with arrays and a later change to the
 * envelope is one edit. A bare array is accepted too.
 */
const unwrap = <T>(key: string) => (data: unknown): T[] => {
  if (Array.isArray(data)) return data as T[];
  const list = (data as Record<string, unknown> | null)?.[key];
  return Array.isArray(list) ? (list as T[]) : [];
};

/** Where a derived capability came from, in words — the backend stores the source, not a sentence. */
const EVIDENCE_FROM: Record<string, string> = {
  programs: 'Derived from one of your programs.',
  program_billing_activities: 'Derived from the service codes your programs bill.',
  funder_book: 'Derived from a funder you already bill.',
  program_import: 'Derived from Program Import.',
  manual: 'Added by your team.',
};

const withReasons = (item: CapabilityItem): CapabilityItem => {
  const ev = item.evidence;
  if (!ev || (Array.isArray(ev.reasons) && ev.reasons.length > 0)) return item;
  const from = typeof ev.from === 'string' ? EVIDENCE_FROM[ev.from] : undefined;
  return from ? { ...item, evidence: { ...ev, reasons: [from] } } : item;
};

interface AttributionWire {
  service?: { service_code?: string | null; item_id?: number | null; label?: string | null } | null;
  existing?: Omit<AttributionFunder, 'is_existing'>[];
  new?: Omit<AttributionFunder, 'is_existing'>[];
  funders?: AttributionFunder[];
}

/** The backend returns existing and new funders separately; the panel shows one ordered list. */
const toAttribution = (w: AttributionWire): AttributionResult => ({
  service_code: w.service?.service_code ?? null,
  item_id: w.service?.item_id ?? null,
  label: w.service?.label ?? null,
  funders: w.funders ?? [
    ...(w.existing ?? []).map((f) => ({ ...f, is_existing: true })),
    ...(w.new ?? []).map((f) => ({ ...f, is_existing: false })),
  ],
});

export function fundingApi(organizationId: number) {
  const headers = { 'X-CBO-ID': String(organizationId) };
  const get = <T>(path: string) => authFetchJson<T>(base(path), { headers });
  const send = <T>(method: 'POST' | 'PUT' | 'PATCH', path: string, body?: unknown) =>
    authFetchJson<T>(base(path), { method, headers, jsonBody: body ?? {} });

  return {
    summary: () => get<FundingSummary>('/summary'),

    getProfile: () => get<CapabilityProfile>('/profile'),
    saveProfile: (p: Partial<CapabilityProfile>) => send<CapabilityProfile>('PUT', '/profile', p),

    deriveCapabilities: () => send<{ proposed: number }>('POST', '/capabilities/derive'),
    listCapabilities: () => get<unknown>('/capabilities').then(unwrap<CapabilityItem>('capabilities')).then((l) => l.map(withReasons)),
    addCapability: (item: Pick<CapabilityItem, 'kind' | 'label' | 'service_codes' | 'program_types'>) =>
      send<CapabilityItem>('POST', '/capabilities', item),
    updateCapability: (id: number, patch: Partial<Pick<CapabilityItem, 'status' | 'label' | 'service_codes' | 'program_types'>>) =>
      send<CapabilityItem>('PATCH', `/capabilities/${id}`, patch),

    listSources: () => get<unknown>('/sources').then(unwrap<IntentSource>('sources')),
    setSource: (kind: IntentSourceKind, enabled: boolean) => send<IntentSource>('PATCH', `/sources/${kind}`, { enabled }),

    scan: () => send<ScanResult>('POST', '/scan'),
    listOpportunities: () => get<unknown>('/opportunities').then(unwrap<Opportunity>('opportunities')),
    updateOpportunity: (id: number, patch: Partial<Pick<Opportunity, 'status' | 'status_note' | 'contact_name' | 'contact_email'>>) =>
      send<Opportunity>('PATCH', `/opportunities/${id}`, patch),

    attribution: (q: { service_code?: string; item_id?: number }) => {
      const params = new URLSearchParams();
      if (q.service_code) params.set('service_code', q.service_code);
      if (q.item_id != null) params.set('item_id', String(q.item_id));
      return get<AttributionWire>(`/attribution?${params.toString()}`).then(toAttribution);
    },

    draftOutreach: (opportunityId: number) => send<Outreach>('POST', `/opportunities/${opportunityId}/outreach/draft`),
    listOutreach: () => get<unknown>('/outreach').then(unwrap<Outreach>('outreach')),
    updateOutreach: (id: number, patch: Partial<Pick<Outreach, 'subject' | 'body' | 'to_name' | 'to_email'>>) =>
      send<Outreach>('PATCH', `/outreach/${id}`, patch),
    // The backend refuses a send that is not approved; pressing Approve & send is the approval.
    sendOutreach: (id: number) => send<Outreach>('POST', `/outreach/${id}/send`, { approve: true }),
  };
}

export type FundingApi = ReturnType<typeof fundingApi>;

/** Human labels for the intent sources, with what each one honestly is. */
export const SOURCE_COPY: Record<IntentSourceKind, { name: string; detail: string }> = {
  payer_directory: {
    name: 'National payer directory',
    detail: 'Medicaid and Medicare plans on the clearinghouse network, with the states each operates in and the transactions it accepts.',
  },
  program_catalog: {
    name: 'Program catalog',
    detail: 'Public programs imported from CMS, HRSA, state filings and uploads, with their service areas and deadlines.',
  },
  discovered_sponsors: {
    name: 'Discovered sponsors',
    detail: 'Organizations that fund programs, found while importing program data, with a contact where one was published.',
  },
  manual: {
    name: 'Added by your team',
    detail: 'Funders your team enters by hand.',
  },
};
